import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import DraggableList from "src/components/common/DraggableList";
import Button from "src/components/common/Button";
import { ModalActions } from "src/redux/Modal/actions";
import styles from "./styles.module.scss";
import { SettingsSelectors } from "src/redux/Settings/selectors";
import { AuthSelectors } from "src/redux/Auth/selectors";
import { SettingsActions } from "src/redux/Settings/actions";
import { SpinnerActions } from "src/redux/Spinner/actions";
import { ConfirmActions } from "src/redux/Confirm/actions";

const identifiers = {
    ISIN: "ISIN",
    Ticker: "Ticker",
    BbgCode: "BBG Code",
    Name: "English Name",
    LocalName: "Local Name",
};

export default () => {
    const [availableList, setAvailableList] = useState([]);
    const userSettings = useSelector(SettingsSelectors.UserSettings);
    const [shownList, setShownList] = useState([]);
    const [selectedAvailableIdx, setSelectedAvailableIdx] = useState(null);
    const [selectedShownIdx, setSelectedShownIdx] = useState(null);
    const dispatch = useDispatch();
    const isTraderRole = useSelector(AuthSelectors.IsTraderRole);
    const updateUserSettings = (settings) =>
        dispatch(SettingsActions.UpdateUserSettings(settings));

    useEffect(() => {
        const idDisplayOrder = userSettings.idDisplayOrder || [];
        setShownList(
            idDisplayOrder.map((i) => ({ name: identifiers[i], value: i }))
        );
        setAvailableList(
            Object.keys(identifiers)
                .filter((i) => !idDisplayOrder.includes(i))
                .map((i) => ({ name: identifiers[i], value: i }))
        );
    }, [userSettings.idDisplayOrder]);

    const moveTo = (
        fromIdx,
        setFromIdx,
        fromList,
        setFromList,
        setToIdx,
        toList,
        setToList
    ) => {
        if (fromIdx === null) return;
        if (fromIdx >= fromList.length) return;
        const list = fromList.splice(fromIdx, 1);
        setFromList(fromList);
        if (fromList.length === 0) setFromIdx(null);

        const newToList = toList.concat(list);
        setToList(newToList);
        if (newToList.length === 0) setToIdx(null);
    };

    const saveIdentifierPreferences = () => {
        if (shownList.length === 0) {
            dispatch(
                ConfirmActions.Open({
                    message: "You must choose at least one identifier",
                    title: "Error",
                    testId: "identifier-preference-error",
                    warning: true,
                })
            );
            return;
        }
        dispatch(SpinnerActions.Open());
        updateUserSettings({
            ...userSettings,
            idDisplayOrder: shownList.map((l) => l.value),
        })
            .then(() => {
                dispatch(ModalActions.Close());
                dispatch(SpinnerActions.Close());
            })
            .catch(() => {
                dispatch(SpinnerActions.Close());
            });
    };

    return (
        <div className={styles.identifierPreferences}>
            <p className={styles.description}>
                Select the identifiers you wish to see on the Negotiations
                screen and the order you would like to see them. Whichever
                identifier is first will be used for sorting negotiations.
            </p>

            <div className={styles.identifierList}>
                <DraggableList
                    className={styles.left}
                    title={"Available"}
                    list={availableList}
                    setList={setAvailableList}
                    setSelectedIdx={setSelectedAvailableIdx}
                    selectedIdx={selectedAvailableIdx}
                />
                <div className={styles.buttons}>
                    <div
                        className={[
                            styles.moveToRight,
                            !availableList[selectedAvailableIdx] &&
                                styles.disabled,
                        ].join(" ")}
                        data-testid="move-right"
                        onClick={() =>
                            moveTo(
                                selectedAvailableIdx,
                                setSelectedAvailableIdx,
                                availableList,
                                setAvailableList,
                                setSelectedShownIdx,
                                shownList,
                                setShownList
                            )
                        }
                    />
                    <div
                        className={[
                            styles.moveToLeft,
                            !shownList[selectedShownIdx] && styles.disabled,
                        ].join(" ")}
                        data-testid="move-left"
                        onClick={() =>
                            moveTo(
                                selectedShownIdx,
                                setSelectedShownIdx,
                                shownList,
                                setShownList,
                                setSelectedAvailableIdx,
                                availableList,
                                setAvailableList
                            )
                        }
                    />
                </div>
                <DraggableList
                    className="right"
                    title={"Shown"}
                    list={shownList}
                    setList={setShownList}
                    setSelectedIdx={setSelectedShownIdx}
                    selectedIdx={selectedShownIdx}
                    movable
                />
            </div>
            <div key="example" className={styles.example}>
                <div className={styles.rect} />
                Ex) Mystery Inc / 000990.KQ
            </div>
            <div key="footer" className={styles.footer}>
                <Button
                    onClick={() => dispatch(ModalActions.Close())}
                    value="Cancel"
                    color="reversedWhite"
                    dataTestId="cancel-identifier-preferences"
                />
                <Button
                    value="Save"
                    onClick={saveIdentifierPreferences}
                    disabled={!isTraderRole}
                    dataTestId="save-identifier-preferences"
                />
            </div>
        </div>
    );
};
